<template>
  <div class="sentry-test">
    <h1>Sentry 测试页面</h1>

    <div class="test-buttons">
      <el-button type="primary" @click="handleTestMessage">测试消息</el-button>
      <el-button type="warning" @click="handleTestError">测试错误</el-button>
      <el-button type="danger" @click="handleTestException">测试异常</el-button>
      <el-button type="info" @click="handleTestUser">测试用户信息</el-button>
      <el-button type="success" @click="handleTestTag">测试标签</el-button>
    </div>

    <div class="test-info">
      <h3>测试信息</h3>
      <p>环境: {{ environment }}</p>
      <p>版本: {{ version }}</p>
      <p>DSN: {{ dsn }}</p>
      <p>用户: {{ userInfo }}</p>
    </div>

    <div class="console-output">
      <h3>控制台输出</h3>
      <div class="console-content">
        <div v-for="(log, index) in consoleLogs" :key="index" class="log-item">
          <span class="log-time">{{ log.time }}</span>
          <span class="log-level" :class="log.level">{{ log.level }}</span>
          <span class="log-message">{{ log.message }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as Sentry from "@sentry/vue";
export default {
  name: "SentryTest",
  data() {
    return {
      environment: process.env.VUE_APP_BUILD_ENV || "production",
      version: require("../../package.json").version,
      dsn: "https://b8c48dac6cb8df0fd544c5e4cea20642@sentry.upho2015.com/49",
      userInfo: "未设置",
      consoleLogs: [],
    };
  },
  mounted() {
    this.addLog("info", "Sentry测试页面已加载");
    this.checkSentryStatus();
  },
  methods: {
    addLog(level, message) {
      const time = new Date().toLocaleTimeString();

      // 确保level是有效的console方法
      const validLevels = ["log", "info", "warn", "error"];
      let safeLevel = level;

      // 特殊映射
      if (level === "success") {
        safeLevel = "info";
      } else if (level === "warning") {
        safeLevel = "warn";
      } else if (!validLevels.includes(level)) {
        safeLevel = "log";
      }

      this.consoleLogs.push({ time, level: safeLevel, message });

      // 安全地调用console方法
      if (console[safeLevel] && typeof console[safeLevel] === "function") {
        console[safeLevel](message);
      } else {
        console.log(`[${safeLevel.toUpperCase()}] ${message}`);
      }
    },

    checkSentryStatus() {
      console.log("Sentry__", Sentry);

      try {
        // 检查Sentry是否已初始化
        if (Sentry.getCurrentHub().getClient()) {
          this.addLog("info", "Sentry已初始化");
          this.addLog(
            "info",
            `Sentry客户端: ${
              Sentry.getCurrentHub().getClient().constructor.name
            }`
          );
        } else {
          this.addLog("warn", "Sentry未初始化");
        }
      } catch (error) {
        this.addLog("error", `检查Sentry状态失败: ${error.message}`);
      }
    },

    handleTestMessage() {
      try {
        Sentry.captureMessage("这是一条测试消息", "info");
        this.addLog("info", "测试消息已发送到Sentry");
      } catch (error) {
        this.addLog("error", `发送测试消息失败: ${error.message}`);
      }
    },

    handleTestError() {
      try {
        const error = new Error("这是一个测试错误");
        Sentry.captureException(error);
        this.addLog("info", "测试错误已发送到Sentry");
      } catch (error) {
        this.addLog("error", `发送测试错误失败: ${error.message}`);
      }
    },

    handleTestException() {
      try {
        // 故意抛出一个异常
        throw new Error("这是一个故意抛出的异常");
      } catch (error) {
        Sentry.captureException(error);
        this.addLog("info", "异常已捕获并发送到Sentry");
      }
    },

    handleTestUser() {
      try {
        const user = {
          id: "test_user_123",
          username: "testuser",
          name: "测试用户",
        };
        Sentry.setUser(user);
        this.userInfo = JSON.stringify(user);
        this.addLog("info", "用户信息已设置到Sentry");
      } catch (error) {
        this.addLog("error", `设置用户信息失败: ${error.message}`);
      }
    },

    handleTestTag() {
      try {
        Sentry.setTag("test_tag", "test_value");
        Sentry.setTag("test_time", new Date().toISOString());
        this.addLog("info", "标签已设置到Sentry");
      } catch (error) {
        this.addLog("error", `设置标签失败: ${error.message}`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sentry-test {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;

  h1 {
    color: #409eff;
    margin-bottom: 30px;
  }

  .test-buttons {
    margin-bottom: 30px;

    .el-button {
      margin-right: 15px;
      margin-bottom: 10px;
    }
  }

  .test-info {
    background: #f5f7fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 30px;

    h3 {
      margin-top: 0;
      color: #606266;
    }

    p {
      margin: 8px 0;
      font-family: monospace;
    }
  }

  .console-output {
    background: #2d3748;
    color: #e2e8f0;
    padding: 20px;
    border-radius: 8px;
    max-height: 400px;
    overflow-y: auto;

    h3 {
      margin-top: 0;
      color: #63b3ed;
    }

    .console-content {
      font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
      font-size: 14px;
    }

    .log-item {
      margin: 5px 0;
      padding: 5px 0;
      border-bottom: 1px solid #4a5568;

      .log-time {
        color: #a0aec0;
        margin-right: 10px;
      }

      .log-level {
        display: inline-block;
        min-width: 60px;
        text-align: center;
        border-radius: 4px;
        margin-right: 10px;
        font-size: 12px;
        font-weight: bold;

        &.info {
          background: #3182ce;
          color: white;
        }

        &.log {
          background: #4a5568;
          color: white;
        }

        &.info {
          background: #3182ce;
          color: white;
        }

        &.warn {
          background: #d69e2e;
          color: white;
        }

        &.success {
          background: #38a169;
          color: white;
        }

        &.error {
          background: #e53e3e;
          color: white;
        }
      }

      .log-message {
        color: #e2e8f0;
      }
    }
  }
}
</style>
